import React, { useReducer, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import jwt from "jsonwebtoken";
import { useOktaAuth } from "@okta/okta-react";
import { OktaAuth } from "@okta/okta-auth-js";
import LineupCardApi from "../httpClients/LineupCardApi";
import UserApi from "../httpClients/UserApi";
import env from "../env";
import { AuthConstants } from "../constants/AuthConstants";

const oktaAuth = new OktaAuth(env.oidc);
const AuthContext = React.createContext({});

const initialState = {
  loggedIn: false,
  email: "",
  accessToken: "",
  idToken: {},
  groups: [],
  loginError: false,
  mobileNavOpen: false,
  refreshToken: "",
  userInfo: {},
  clubInfo: {},
  club: {},
};

const AuthContextProvider = ({ children }) => {
  const reducer = (state, action) => {
    switch (action.type) {
      case "login":
        const { accessToken, idToken } = JSON.parse(localStorage.getItem("okta-token-storage"));
        const emailAddress = idToken.claims.email;
        const groups = jwt.decode(accessToken.accessToken).groups;
        if (groups && groups.length > 0) {
          if (!groups.includes("Lineupcard-Users") && !groups.includes("Lineupcard-Admin")) {
            return {
              ...state,
              authToken: accessToken.accessToken,
              email: emailAddress,
              loggedIn: false,
              userInfo: {},
              groups: [],
            };
          } else {
            return {
              ...state,
              authToken: accessToken.accessToken,
              email: emailAddress,
              loggedIn: true,
              userInfo: {},
              groups: groups,
            };
          }
        }
        return {
          ...state,
          authToken: accessToken.accessToken,
          email: emailAddress,
          loggedIn: false,
          userInfo: {},
          groups: groups,
        };
      case "logout":
        localStorage.clear();
        return {
          loggedIn: false,
          email: "",
          authToken: "",
          refreshToken: "",
          userData: {},
        };
      case "setGroups":
        return { ...state, groups: action.groups };
      case "setUserInfo":
        localStorage.setItem("userInfo", JSON.stringify(action.userInfo));
        return { ...state, userInfo: action.userInfo, club: action.userInfo.club };
      case "setClubInfo":
        const clubInfoArr = action.clubInfo
          ?.map((club) => {
            return { label: club.abbr, value: club.id, name: club.name };
          })
          .sort((clubA, clubB) => clubA.label.localeCompare(clubB.label));
        localStorage.setItem("clubInfo", JSON.stringify(clubInfoArr));
        return { ...state, clubInfo: clubInfoArr };
      case "checkUserInfo":
        break;
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const { authState } = useOktaAuth();

  const getClubInfo = (clubType) => {
    LineupCardApi.getClubInfo(clubType).then((response) => {
      dispatch({ type: "setClubInfo", clubInfo: response });
    });
  };

  const getUserInfo = () => {
    UserApi.getUserInfo().then((data) => {
      dispatch({ type: "setUserInfo", userInfo: data });
    });
  };

  const hasRole = (role) => {
    return state.userInfo?.roles?.includes(role) ?? false;
  };

  const isAdmin = () => {
    return (
      hasRole(AuthConstants.ROLES.AFL_ADMIN) ||
      hasRole(AuthConstants.ROLES.WBC_ADMIN) ||
      hasRole(AuthConstants.ROLES.ADMIN)
    );
  };

  const isClub = () => {
    // Note: we use to check "Lineupcard-Users" role to determine if the user is a club user but that is a generic okta role,
    // we want our db roles to control this, but if something messese up we can add back the "Lineupcard-Users" role check
    return hasRole(AuthConstants.ROLES.AFL) || hasRole(AuthConstants.ROLES.WBC) || hasRole(AuthConstants.ROLES.MLB);
  };

  useEffect(() => {
    if (authState && authState.isAuthenticated && !state.loggedIn) {
      dispatch({
        type: "login",
      });
      getUserInfo();
    }
  }, [authState]);

  useEffect(() => {
    if (hasRole(AuthConstants.ROLES.AFL_ADMIN)) {
      getClubInfo("AFL");
    } else if (hasRole(AuthConstants.ROLES.WBC_ADMIN)) {
      getClubInfo("WBC");
    } else {
      getClubInfo("MLB");
    }
  }, [JSON.stringify(state.userInfo?.roles)]);

  return <AuthContext.Provider value={{ state, dispatch, hasRole, isAdmin, isClub }}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("useAuth must be used within a AuthContextProvider");
  }
  return authContext;
};

AuthContextProvider.propTypes = {
  children: PropTypes.object.isRequired,
};

export { AuthContext, AuthContextProvider, useAuth, oktaAuth };
